/**
 * #714: the fixed words this package's components show (Cancel, Close, "No
 * results", "1–20 of 120"…), in the reader's language.
 *
 * WHY A CONTEXT AND NOT LINGUI HERE. Lingui collects a word only where an app
 * writes it with its macros, and each app has its own catalog; this package is
 * built on its own and is in no catalog. So the words are a plain object with
 * English defaults, and an app that translates provides the whole object once,
 * near its root, from its own catalog (admin: `lib/ui-labels.tsx`). An app that
 * does not (ops, learn, terminal) gets English with no change.
 *
 * A component reads `useUiLabels()` and uses a word only where the caller did
 * not pass its own (a `confirmLabel` prop still wins).
 *
 * Words with a value in them are functions, so the app can put the value where
 * its language wants it.
 */
import * as React from 'react';
import { formatExact, formatRelativeTime, formatStamp } from '../lib/format.js';
import { DATE_PRESET_LABELS, type DatePreset } from '../lib/date-presets.js';
import type { RecencyBucket } from '../lib/notification-grouping.js';

export interface UiLabels {
  // Dialogs and actions.
  cancel: string;
  confirm: string;
  close: string;
  save: string;
  working: string;
  saving: string;
  pleaseConfirm: string;
  enterANumber: string;
  // Tables and lists.
  search: string;
  nothingHereYet: string;
  table: string;
  noResults: string;
  selectEveryRowOnPage: string;
  selectThisRow: string;
  selectRow: (row: string) => string;
  rangeOfTotal: (from: number, to: number, total: number) => string;
  // Uploads.
  uploading: string;
  uploadFailed: string;
  couldNotReadImage: string;
  chooseImageType: string;
  selectedImage: string;
  // Dialogs and actions (more).
  remove: string;
  dismiss: string;
  /** #912: the page header's "⋯" button. */
  moreActions: string;
  loading: string;
  to: string;
  clearAll: string;
  fieldRequired: (label: string) => string;
  enterField: (label: string) => string;
  mustBeAtLeast: (min: number) => string;
  mustBeAtMost: (max: number) => string;
  // Data table.
  previous: string;
  next: string;
  pageOfLast: (page: number, last: number) => string;
  // Image upload.
  clickToUpload: string;
  orDragAnImage: string;
  imageTypesHint: string;
  imageAttached: string;
  // Notification bell.
  notifications: string;
  markAllRead: string;
  all: string;
  nothingUnread: string;
  allCaughtUp: string;
  switchToAll: string;
  notificationsWillLandHere: string;
  noLinkedPage: string;
  unreadNotifications: (n: number) => string;
  unreadWithCount: (n: number) => string;
  nTimes: (n: number) => string;
  // Report a bug.
  reportABug: string;
  thanksThatsWithUs: string;
  reportSentBody: string;
  reportBugIntro: string;
  whatHappened: string;
  bugTitlePlaceholder: string;
  whatWereYouDoing: string;
  bugDescriptionPlaceholder: string;
  howMuchBlocking: string;
  severityBlocker: string;
  severityBlockerHint: string;
  severityMajor: string;
  severityMajorHint: string;
  severityMinor: string;
  severityMinorHint: string;
  addScreenshotOrFile: string;
  addAtMostThreeFiles: string;
  fileCouldNotBeRead: string;
  reportBugRecordedContext: string;
  couldNotSendReport: string;
  sendToMangrove: string;
  removeFile: (name: string) => string;
  // #990: profiles.
  audienceEveryone: string;
  audienceCofounders: string;
  audienceOnlyMe: string;
  workingDays: string;
  workFrom: string;
  workTo: string;
  notSetYet: string;
  addSkill: string;
  searchTimeZones: string;
  breadcrumb: string;
  // Activity timeline.
  today: string;
  yesterday: string;
  system: string;
  emptyValue: string;
  setTo: string;
  changedTo: string;
  nothingHappenedYet: string;
  showEarlier: string;
  // Bulk bar.
  bulkActions: string;
  clearSelection: string;
  nSelected: (n: number) => string;
  bulkDone: (done: number, verb: string) => string;
  bulkNotDone: (n: number, verb: string) => string;
  // Filter bar.
  filterAnyTime: (label: string) => string;
  filterAll: (label: string) => string;
  filterBetweenDates: (label: string) => string;
  filterValue: (label: string, value: string) => string;
  filterFrom: (label: string) => string;
  filterTo: (label: string) => string;
  removeFilter: (label: string, value: string) => string;
  // Filter chips, searchable select, bottom-sheet filters (#913).
  clearFilter: (label: string) => string;
  filterButton: string;
  filterButtonCount: (n: number) => string;
  filtersTitle: string;
  applyFilters: string;
  selectOption: string;
  clearSelectedOption: string;
  // Page help.
  hideHelp: string;
  howThisWorks: string;
  pageHelpLabel: (title: string) => string;
  // Times and dates.
  /** "5 minutes ago": an app passes the reader's language to the browser's own words. */
  relativeTime: (date: Date | string, now?: Date) => string;
  /** "1:24 PM" / "Yesterday" / "Oct 9" (#918): a list or message time, never seconds. */
  stamp: (date: Date | string, now?: Date) => string;
  /** "Oct 9, 2026, 1:24 PM": the exact time, for a tooltip or an audit row. */
  exactTime: (date: Date | string) => string;
  /** A notification section's heading; the bucket itself stays the English id. */
  recencyBucket: (bucket: RecencyBucket) => string;
  datePresets: Record<DatePreset, string>;
  rangeFrom: (date: string) => string;
  rangeUntil: (date: string) => string;
  // Waiting for the server (WakingUpNotice).
  waitingForServer: string;
  serverSlowToAnswer: string;
  serverCheckingBody: string;
  serverUnreachableBody: string;
  serverBusyBody: string;
  stillWaitingBody: string;
  stillWaitingBusyBody: string;
  keepWorking: string;
  secondsShort: (seconds: number) => string;
  waitPillLabel: (heading: string, seconds: number) => string;
  // Sidebar toggle (#886).
  collapseSidebar: string;
  expandSidebar: string;
  // The dock's names toggle and page trays (#926).
  showNames: string;
  hideNames: string;
  modulePages: (module: string) => string;
  // The two looks (#925).
  appearance: string;
  appearanceWarm: string;
  appearanceWarmHint: string;
  appearanceQuiet: string;
  appearanceQuietHint: string;
  // One Appearance panel (#931).
  appearanceLook: string;
  colorMode: string;
  colorModeLight: string;
  colorModeDark: string;
  colorModeSystem: string;
  density: string;
  densityComfortable: string;
  densityCompact: string;
  dockNamesTitle: string;
  dockIconsOnly: string;
  dockWithNames: string;
  // Where the dock sits, corners and depth (#938, #939).
  dockPositionTitle: string;
  dockLeft: string;
  dockBottom: string;
  dockRight: string;
  corners: string;
  cornersLook: string;
  cornersSharp: string;
  cornersSoft: string;
  cornersRound: string;
  depth: string;
  depthLook: string;
  depthFlat: string;
  depthSoft: string;
  depthDeep: string;
  // Colour themes and your own colours (#937, #938).
  colourTheme: string;
  followWorkspaceTheme: (workspaceTheme: string) => string;
  themeFamilyMangrove: string;
  themeFamilyGreen: string;
  themeFamilyOther: string;
  themeFamilySaved: string;
  ownColours: string;
  ownColoursHint: string;
  ownColourPrimary: string;
  ownColourAccent: string;
  ownColourSidebar: string;
  ownColourCanvas: string;
  ownColoursForLight: string;
  ownColoursForDark: string;
  ownColoursHardToRead: string;
  ownColoursFixIt: string;
  ownColoursReset: string;
  saveAsTheme: string;
  themeNamePlaceholder: string;
  savedThemeBy: (name: string) => string;
  deleteTheme: string;
  renameTheme: string;
  font: string;
  workspaceFont: string;
}

export const UI_LABELS_EN: UiLabels = {
  cancel: 'Cancel',
  confirm: 'Confirm',
  close: 'Close',
  save: 'Save',
  working: 'Working…',
  saving: 'Saving…',
  pleaseConfirm: 'Please confirm this action.',
  enterANumber: 'Enter a number.',
  search: 'Search…',
  nothingHereYet: 'Nothing here yet',
  table: 'Table',
  noResults: 'No results',
  selectEveryRowOnPage: 'Select every row on this page',
  selectThisRow: 'Select this row',
  selectRow: (row) => `Select ${row}`,
  rangeOfTotal: (from, to, total) => `${from}–${to} of ${total}`,
  uploading: 'Uploading…',
  uploadFailed: 'Upload failed.',
  couldNotReadImage: 'Could not read the image.',
  chooseImageType: 'Please choose a PNG, JPEG, or WebP image.',
  selectedImage: 'Selected image',
  // Dialogs and actions (more).
  remove: 'Remove',
  dismiss: 'Dismiss',
  moreActions: 'More actions',
  loading: 'Loading…',
  to: 'to',
  clearAll: 'Clear all',
  fieldRequired: (label) => `${label} is required.`,
  enterField: (label) => `Enter ${label}.`,
  mustBeAtLeast: (min) => `Must be ${min} or more.`,
  mustBeAtMost: (max) => `Must be ${max} or less.`,
  // Data table.
  previous: 'Prev',
  next: 'Next',
  pageOfLast: (page, last) => `Page ${page} / ${last}`,
  // Image upload.
  clickToUpload: 'Click to upload',
  orDragAnImage: 'or drag an image',
  imageTypesHint: 'PNG, JPEG, or WebP',
  imageAttached: 'Image attached',
  // Notification bell.
  notifications: 'Notifications',
  markAllRead: 'Mark all read',
  all: 'All',
  nothingUnread: 'Nothing unread',
  allCaughtUp: "You're all caught up",
  switchToAll: 'Switch to All to see everything from before.',
  notificationsWillLandHere: 'Alerts, mentions, and assignments will land here.',
  noLinkedPage: 'No linked page — click to mark as read',
  unreadNotifications: (n) => `${n} unread notifications`,
  unreadWithCount: (n) => `Unread (${n})`,
  nTimes: (n) => `${n} times`,
  // Report a bug.
  reportABug: 'Report a bug',
  thanksThatsWithUs: "Thanks — that's with us",
  reportSentBody:
    'It went straight to the Mangrove team with the page you were on. We may follow up if we need to reproduce it.',
  reportBugIntro:
    "Tell us what went wrong. It goes to the Mangrove team, and your account's administrators can read it too.",
  whatHappened: 'What happened?',
  bugTitlePlaceholder: 'Export CSV downloads an empty file',
  whatWereYouDoing: 'What were you doing, and what did you expect?',
  bugDescriptionPlaceholder:
    'I filtered the list and clicked Export CSV.\nI expected 6 rows; the file had none.',
  howMuchBlocking: 'How much is it blocking you?',
  severityBlocker: 'Blocker',
  severityBlockerHint: 'I cannot carry on working',
  severityMajor: 'Major',
  severityMajorHint: 'Wrong or missing, but I can work around it',
  severityMinor: 'Minor',
  severityMinorHint: 'Cosmetic or a small annoyance',
  addScreenshotOrFile: 'Add a screenshot or file (optional)',
  addAtMostThreeFiles: 'Add at most 3 files to one report.',
  fileCouldNotBeRead: 'That file could not be read.',
  reportBugRecordedContext:
    "We also record the page you're on and your browser version, so you don't have to describe them.",
  couldNotSendReport: 'Could not send the report. Please try again.',
  sendToMangrove: 'Send to Mangrove',
  removeFile: (name) => `Remove ${name}`,
  audienceEveryone: 'Everyone in HQ',
  audienceCofounders: 'Cofounders',
  audienceOnlyMe: 'Only me',
  workingDays: 'Working days',
  workFrom: 'From',
  workTo: 'To',
  notSetYet: 'Not set yet',
  addSkill: 'Add a skill',
  searchTimeZones: 'Search for a city or time zone',
  breadcrumb: 'Breadcrumb',
  // Activity timeline.
  today: 'Today',
  yesterday: 'Yesterday',
  system: 'System',
  emptyValue: '(empty)',
  setTo: 'set to',
  changedTo: 'changed to',
  nothingHappenedYet: 'Nothing has happened to this record yet.',
  showEarlier: 'Show earlier',
  // Bulk bar.
  bulkActions: 'Bulk actions',
  clearSelection: 'Clear selection',
  nSelected: (n) => `${n} selected`,
  bulkDone: (done, verb) => `${done} ${verb}.`,
  bulkNotDone: (n, verb) => `${n} not ${verb}:`,
  // Filter bar.
  filterAnyTime: (label) => `${label}: any time`,
  filterAll: (label) => `${label}: all`,
  filterBetweenDates: (label) => `${label}: between dates…`,
  filterValue: (label, value) => `${label}: ${value}`,
  filterFrom: (label) => `${label} from`,
  filterTo: (label) => `${label} to`,
  removeFilter: (label, value) => `Remove filter ${label}: ${value}`,
  clearFilter: (label) => `Clear ${label}`,
  filterButton: 'Filter',
  filterButtonCount: (n) => `Filter (${n})`,
  filtersTitle: 'Filters',
  applyFilters: 'Apply',
  selectOption: 'Select…',
  clearSelectedOption: 'Clear',
  // Page help.
  hideHelp: 'Hide help ▴',
  howThisWorks: 'How this works ▾',
  pageHelpLabel: (title) => `${title} help`,
  // Times and dates.
  relativeTime: (date, now) => formatRelativeTime(date, now),
  stamp: (date, now) => formatStamp(date, now),
  exactTime: (date) => formatExact(date),
  recencyBucket: (bucket) => bucket,
  datePresets: DATE_PRESET_LABELS,
  rangeFrom: (date) => `from ${date}`,
  rangeUntil: (date) => `until ${date}`,
  // Waiting for the server.
  waitingForServer: 'Waiting for the server',
  serverSlowToAnswer: 'The server is slow to answer',
  serverCheckingBody: 'It has not answered yet. Checking whether it is up…',
  serverUnreachableBody:
    'It has not answered. Either it is starting up after a quiet spell, which takes a few seconds, or the connection is slow. Nothing is lost; this will carry on by itself.',
  serverBusyBody:
    'It is up and running, so this is not your connection: something on our side is taking longer than it should. Nothing is lost; this will carry on by itself.',
  stillWaitingBody:
    'Still waiting, longer than usual. Nothing is lost; this will carry on by itself.',
  stillWaitingBusyBody:
    'Still waiting. The server is up, so this is on our side, not yours. Nothing is lost; this will carry on by itself.',
  keepWorking: 'Keep working',
  secondsShort: (seconds) => `${seconds}s`,
  waitPillLabel: (heading, seconds) => `${heading}, ${seconds} seconds. Show details`,
  // Sidebar toggle (#886).
  collapseSidebar: 'Collapse sidebar',
  expandSidebar: 'Expand sidebar',
  // The dock's names toggle and page trays (#926).
  showNames: 'Show names',
  hideNames: 'Hide names',
  modulePages: (module) => `${module} pages`,
  // The two looks (#925).
  appearance: 'Appearance',
  appearanceWarm: 'Warm depth',
  appearanceWarmHint: 'A warm page, soft shadows and roomy rows.',
  appearanceQuiet: 'Quiet precision',
  appearanceQuietHint: 'A near-white page, flat surfaces and compact rows.',
  // One Appearance panel (#931).
  appearanceLook: 'Look',
  colorMode: 'Light or dark',
  colorModeLight: 'Light',
  colorModeDark: 'Dark',
  colorModeSystem: 'Match this device',
  density: 'Density',
  densityComfortable: 'Comfortable',
  densityCompact: 'Compact',
  dockNamesTitle: 'Names in the dock',
  dockIconsOnly: 'Icons only',
  dockWithNames: 'Icons and names',
  dockPositionTitle: 'Where the dock sits',
  dockLeft: 'Left',
  dockBottom: 'Bottom',
  dockRight: 'Right',
  corners: 'Corners',
  cornersLook: 'The look’s own',
  cornersSharp: 'Sharp',
  cornersSoft: 'Soft',
  cornersRound: 'Round',
  depth: 'Shadows',
  depthLook: 'The look’s own',
  depthFlat: 'Flat',
  depthSoft: 'Soft',
  depthDeep: 'Deep',
  colourTheme: 'Colour theme',
  followWorkspaceTheme: (workspaceTheme) => `Follow the workspace (${workspaceTheme})`,
  themeFamilyMangrove: 'Mangrove',
  themeFamilyGreen: 'Greens',
  themeFamilyOther: 'Other colours',
  themeFamilySaved: 'Saved here',
  ownColours: 'Your own colours',
  ownColoursHint:
    'Start from the theme above and change any of these. The page shows them as you go.',
  ownColourPrimary: 'Brand colour',
  ownColourAccent: 'Highlight',
  ownColourSidebar: 'Dock',
  ownColourCanvas: 'Page',
  ownColoursForLight: 'In light',
  ownColoursForDark: 'In dark',
  ownColoursHardToRead: 'Some text would be hard to read',
  ownColoursFixIt: 'Fix it',
  ownColoursReset: 'Back to the theme’s colours',
  saveAsTheme: 'Save as a theme',
  themeNamePlaceholder: 'Name, e.g. Night swamp',
  savedThemeBy: (name) => `Saved by ${name}`,
  deleteTheme: 'Delete theme',
  renameTheme: 'Rename theme',
  font: 'Font',
  workspaceFont: 'The workspace’s font',
};

const UiLabelsContext = React.createContext<UiLabels>(UI_LABELS_EN);

/** Provide the words once, near the app's root. Missing keys stay English. */
export function UiLabelsProvider({
  labels,
  children,
}: {
  labels: Partial<UiLabels>;
  children: React.ReactNode;
}): React.JSX.Element {
  const value = React.useMemo(() => ({ ...UI_LABELS_EN, ...labels }), [labels]);
  return <UiLabelsContext.Provider value={value}>{children}</UiLabelsContext.Provider>;
}

/** The words for the reader's language (English when no provider is mounted). */
export function useUiLabels(): UiLabels {
  return React.useContext(UiLabelsContext);
}
